// Lib
import React, { useMemo } from 'react';
import { Typography, TextareaAutosize } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Components
import Button from '../../Button';

// Hooks
import useUser from '../../../hooks/useUser';
import useUI from '../../../hooks/useUI';

// Validation schema
import { overLimitValidationSchema } from '../../../validationSchema';

import use2Modal from '../../../hooks/use2Modal';
import ErrorMessage from '../../Error/ErrorMessage';

// Styles
import { useStyles } from './RequestCardStyles';

const TEXTAREA_MIN_ROWS_SMALL_MOBILE = 5;
const TEXTAREA_MIN_ROWS = 8;

const RequestCard = ({
  onSubmit = () => {},
  onChange = () => {},
  loading,
  minLengthOverLimitRequest,
  savedRequest,
  isOverLimitRequest,
  apiError,
}) => {
  const { isSmallMobileMode } = useUI();
  const { isKiteUser } = useUser();
  const { classes, cx } = useStyles({ isKiteUser });
  const { onClose } = use2Modal();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(overLimitValidationSchema(minLengthOverLimitRequest)),
    defaultValues: {
      request: savedRequest || '',
    },
  });

  const error = useMemo(() => errors?.request?.message, [errors?.request]);

  return (
    <div className={classes.root} data-testid="request-modal">
      <Typography variant="h3" className={classes.title}>
        {isOverLimitRequest
          ? 'Your order total is more than your remaining predetermined allocation.'
          : 'You have chosen a shipping method that requires approval.'}
      </Typography>
      <div className={classes.message}>{`Please submit ${
        isOverLimitRequest ? 'an extension' : 'a shipping'
      }  request.`}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="request"
          control={control}
          render={({ field }) => (
            <>
              <div className={classes.fieldName}>
                Request <span className={classes.accent}>*</span>
              </div>
              <TextareaAutosize
                placeholder="Type a request"
                className={cx(classes.textArea, classes.textAreaWidth)}
                minRows={
                  isSmallMobileMode
                    ? TEXTAREA_MIN_ROWS_SMALL_MOBILE
                    : TEXTAREA_MIN_ROWS
                }
                aria-label="empty textarea"
                maxLength={256}
                {...field}
                onChange={onChange(field.onChange)}
              />
            </>
          )}
        />
        {(error || apiError) && <ErrorMessage message={error || apiError} />}
        <div className={classes.buttons}>
          <Button
            variant="text"
            onClick={onClose}
            data-testid="request-modal-cancel-button">
            Cancel
          </Button>
          <Button
            loadingIndicator="Loading…"
            loading={loading}
            type="submit"
            data-testid="request-modal-submit-button">
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RequestCard;
